import React, { useEffect, useState, useRef } from 'react';
import { Form } from 'react-form';
import PropTypes, { any } from 'prop-types';
import { useSelector } from 'react-redux';
import moment from 'moment';
import Modal from '../../../components/Modal/ReactBootstrapModal';

import { objectHasValues } from '../../../utils/misc';
import { Select, CheckBox } from '../../../components/Form';
import { getRefundDataForEvent } from '../../../utils/events';
import { getApiAxiosInstance } from '../../../utils/network';

import { getLabelByTypeId } from '../../../utils/types';
import { getPlayerRegStatus } from '../../../utils';
import { titleCase } from '../../../utils/formatting';
import { paymentStatusTypeIds, registrationStatusTypeIds, sportTypeIds } from '../../../utils/eventConstants';

import './stripeWithdraw.scss';

const WithdrawOptions = {
    WITHDRAW_WITHOUT_REFUND: 1,
    WITHDRAW_AND_REFUND: 2,
    REFUND_ONLY: 3,
};

// supports v2 typeIds
const StripeWithdrawModalV2 = (props) => {
    const { withdrawPlayer, confirmPopup, onCloseClick, divisions, currency, regPaymentRefund, event } = props;

    const formApiRef = useRef();
    const [step, setStep] = useState(1);
    const [playerReceivedDiscounts, setPlayerReceivedDiscounts] = useState(false);
    const [selectedDivisions, setSelectedDivisions] = useState([]);
    const [promoCodesUsed, setPromoCodesUsed] = useState([]);
    const [totalPercentFee, setTotalPercentFee] = useState(0);
    const [totalVerificationFee, setTotalVerificationFee] = useState(0);
    const [totalRefundAmount, setTotalRefundAmount] = useState(0);
    const [totalOrganizerFee, setTotalOrganizerFee] = useState(0);
    const [totalServiceFee, setTotalServiceFee] = useState(0);
    const [totalSMSFee, setTotalSMSFee] = useState(0);
    const [originalSubTotal, setOriginalSubTotal] = useState(0);
    const [totalPaidAmount, setTotalPaidAmount] = useState(0);
    const [formattedCancellationFee, setFormattedCancellationFee] = useState();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [chargeWithdrawalFee, setChargeWithdrawalFee] = useState(true);

    const registeredDivisionIds = withdrawPlayer.divisions
        .filter(d => !(d.registrationStatusTypeId === registrationStatusTypeIds.WITHDRAWN &&
            (d.paymentStatusTypeId === paymentStatusTypeIds.REFUNDED ||
                d.paymentStatusTypeId === paymentStatusTypeIds.REFUNDING)))
        .map(d => d.id);
    const registeredDivisions = divisions.filter(d => {
        return registeredDivisionIds.includes(d.id);
    });
    const coreUI = useSelector(({ coreUI }) => coreUI);
    const { tms: { division: { paymentStatusTypes } } } = coreUI.types;

    const { withdrawalFee, effectiveAfterDateTimeUtc } = event?.registrationCancellationPolicy || {};
    const eventHasWithdrawalFee = withdrawalFee && withdrawalFee > 0;
    const withdrawalFeeIsEffectiveNow = effectiveAfterDateTimeUtc
        ? moment.utc().isSameOrAfter(moment.utc(effectiveAfterDateTimeUtc))
        : true;

    const playerIsWithdrawnFromAllDivisions = (values) => {
        // player has been withdrawn from all divisions
        const objectValues = Object.values(values || {});
        return objectValues.length === registeredDivisions.length &&
            objectValues.every(value => [WithdrawOptions.WITHDRAW_WITHOUT_REFUND, WithdrawOptions.WITHDRAW_AND_REFUND].includes(parseInt(value)));
    };

    const showWithdrawalFeeWaiverCheckbox = (values) => {
        const withdrawnFromAllDivisions = playerIsWithdrawnFromAllDivisions(values);
        return eventHasWithdrawalFee && withdrawalFeeIsEffectiveNow && withdrawnFromAllDivisions;
    };

    const getRefundData = async () => {
        const apiInstance = getApiAxiosInstance();
        const eventId = event?.id;
        const refundPostBodyRequest = {
            divisionIds: selectedDivisions,
            memberId: withdrawPlayer.memberId,
        };

        if (eventHasWithdrawalFee && withdrawalFeeIsEffectiveNow && playerIsWithdrawnFromAllDivisions(formApiRef.current?.values)) {
            refundPostBodyRequest.isCancellationFeeWaived = !chargeWithdrawalFee;
        }
        const { totalDiscountsPlayerReceived, promoCodesUsed, totalRefundAmount, percentageFeeRefundTotal, verificationFeeRefundTotal, organizerRefundTotal, serviceFeeRefundTotal, smsFeeRefundTotal, originalSubTotal, totalAmountPaid, formattedCancellationFeeTotal } = await getRefundDataForEvent(apiInstance, eventId, refundPostBodyRequest);
        setTotalPercentFee(percentageFeeRefundTotal);
        setTotalVerificationFee(verificationFeeRefundTotal);
        setTotalRefundAmount(totalRefundAmount);
        setTotalPaidAmount(totalAmountPaid);
        setTotalOrganizerFee(organizerRefundTotal);
        setTotalServiceFee(serviceFeeRefundTotal);
        setTotalSMSFee(smsFeeRefundTotal);
        setOriginalSubTotal(originalSubTotal);
        setFormattedCancellationFee(formattedCancellationFeeTotal);

        if (totalDiscountsPlayerReceived > 0) {
            setPlayerReceivedDiscounts(true);
            setPromoCodesUsed(promoCodesUsed);
        } else {
            setPlayerReceivedDiscounts(false);
        }
    };
    useEffect(() => {
        if (selectedDivisions.length > 0) {
            getRefundData();
        }
    }, [selectedDivisions]);

    const defaultValues = {};

    const paidOptions = [
        {
            id: WithdrawOptions.WITHDRAW_WITHOUT_REFUND,
            label: 'Withdraw without Refund',
        },
        {
            id: WithdrawOptions.WITHDRAW_AND_REFUND,
            label: 'Withdraw and Refund',
        },
        {
            id: WithdrawOptions.REFUND_ONLY,
            label: 'Refund Only',
        },
    ];

    const freeOptions = [
        {
            id: WithdrawOptions.WITHDRAW_WITHOUT_REFUND,
            label: 'Withdraw without Refund',
        },
    ];

    const withdrawnPaidOptions = [
        {
            id: WithdrawOptions.REFUND_ONLY,
            label: 'Refund Only',
        },
    ];

    const entryFeePaidOptions = paidOptions.filter(o => o.id !== WithdrawOptions.WITHDRAW_WITHOUT_REFUND);

    const handlePreviousStepClick = () => {
        setStep(1);
    };

    const handleChargeWithdrawalFeeClick = () => {
        setChargeWithdrawalFee(!chargeWithdrawalFee);
    };

    const filterDivisions = (values, withdraws) => {
        // return withdraws
        if (withdraws) {
            return divisions.filter(d => {
                return values[d.id] && (values[d.id] === '1' || values[d.id] === '2');
            });
        }
        // return refunds
        return divisions.filter(d => {
            return values[d.id] && (values[d.id] === '2' || values[d.id] === '3');
        });
    };

    const displayDivisions = (values, withdraws) => {
        const filteredDivisions = filterDivisions(values, withdraws);
        if (regPaymentRefund?.divisionAmounts?.length > 0 && filteredDivisions?.length > 0) {
            const divisionList = [];
            filteredDivisions.forEach(div => {
                divisionList.push({
                    id: div.id,
                    name: div.name,
                    price: div.price,
                });
                const divisionAmount = regPaymentRefund.divisionAmounts.find(d => div.isEventEntryFeeDivision && parseInt(d.divisionId) === parseInt(div.id));
                if (divisionAmount) {
                    const index = divisionList.findIndex(l => l.id === div.id);
                    if (index !== -1) {
                        divisionList[index].price = divisionAmount.price;
                    }
                }
            });
            return divisionList.map(d => <li key={d.id} className="text-dark">{d.name} ({d.price})</li>);
        }
        return filteredDivisions.map(d => <li key={d.id} className="text-dark">{d.name} ({d.price})</li>);
    };

    const displayRefunds = () => {
        const totalPaymentWithDiscounts = `${currency.symbol}${parseFloat(totalPaidAmount).toFixed(2)}`;
        const eventFeesToBeRefunded = `${currency.symbol}${parseFloat(totalRefundAmount - totalVerificationFee).toFixed(2)}`;
        const organizerTotal = `${currency.symbol}${parseFloat(totalOrganizerFee).toFixed(2)}`;
        const totalPercentFees = `${currency.symbol}${parseFloat(totalPercentFee).toFixed(2)}`;
        const verifiedFees = `${currency.symbol}${parseFloat(totalVerificationFee).toFixed(2)}`;
        const totalRefund = `${currency.symbol}${parseFloat(totalRefundAmount).toFixed(2)}`;
        const displayedServiceFees = `${currency.symbol}${parseFloat(totalServiceFee).toFixed(2)}`;
        const displayedSmsFees = `${currency.symbol}${parseFloat(totalSMSFee).toFixed(2)}`;
        const displayedOriginalFees = `${currency.symbol}${parseFloat(originalSubTotal).toFixed(2)}`;

        return (
            <>
                {playerReceivedDiscounts && (
                    <p className="mt40">The original event fees from the checkout(s) were: <span className="text-dark">{displayedOriginalFees}</span></p>
                )}
                <p className={`${playerReceivedDiscounts ? '' : 'mt40'}`}> The player paid: <span className="text-dark">{totalPaymentWithDiscounts}</span> {playerReceivedDiscounts ? `using promo code ${promoCodesUsed}` : ''}
                </p>
                <p className="mt16">Event Fees to be Refunded: <span className="text-dark">{eventFeesToBeRefunded}</span>
                </p>
                <ul>
                    <li>
                        Total Percent Fees: {totalPercentFees}
                    </li>
                    <li>
                        Organizer Total: {organizerTotal}
                    </li>
                    <li>
                        Total SMS Fees: {displayedSmsFees}
                    </li>
                    <li>
                        Total Service Fees: {displayedServiceFees}
                    </li>

                </ul>
                <p className="">{event?.sportTypeId === sportTypeIds.PICKLEBALL ? `Transaction Fee` : `Verified Fee`}: <span className="text-dark">{verifiedFees}</span></p>
                {!!formattedCancellationFee && (
                    <p className="">Withdrawal Fee: <span className="text-dark">{formattedCancellationFee}</span></p>
                )}
                <p className="text-bold text-dark mt24">Total refund: {totalRefund}</p>
            </>
        );
    };

    const handleSubmitClick = ({ values }) => {
        const withdrawWithoutRefundValue = '1';
        const withdrawAndRefundValue = '2';
        const refundValue = '3';

        if (step === 1 && objectHasValues(values)) {
            setStep(2);
            const divisionIdsFromReactFormValues = Object.keys(values)
                .filter(key => values[key] === withdrawAndRefundValue || values[key] === refundValue) // Filter keys with values '2' or '3'
                .map(key => parseInt(key, 10));
            setSelectedDivisions(divisionIdsFromReactFormValues);
        } else {
            setIsSubmitting(true);
            const stripeWithdrawReq = {
                withdrawRequest: {
                    memberIds: [withdrawPlayer.memberId],
                    divisionIds: [],
                },
                refundRequest: {
                    memberIds: [withdrawPlayer.memberId],
                    divisionIds: [],
                },
                refundReason: `Event withdrawal requested by player/organizer.`,
            };
            if (eventHasWithdrawalFee && withdrawalFeeIsEffectiveNow && playerIsWithdrawnFromAllDivisions(formApiRef.current?.values)) {
                stripeWithdrawReq.refundRequest.isCancellationFeeWaived = !chargeWithdrawalFee;
            }
            Object.entries(values).forEach(([divisionId, option]) => {
                if (option === withdrawWithoutRefundValue) { // Withdraw without Refund
                    stripeWithdrawReq.withdrawRequest.divisionIds.push(divisionId);
                } else if (option === withdrawAndRefundValue) { // Withdraw and Refund
                    stripeWithdrawReq.withdrawRequest.divisionIds.push(divisionId);
                    stripeWithdrawReq.refundRequest.divisionIds.push(divisionId);
                } else if (option === refundValue) { // Refund Only
                    stripeWithdrawReq.refundRequest.divisionIds.push(divisionId);
                } else {
                    // do nothing
                }
            });
            confirmPopup(stripeWithdrawReq);
        }
    };

    return (
        <Form onSubmit={values => handleSubmitClick({ values })} defaultValues={defaultValues}>
            {(formApi) => {
                const { submitForm, values } = formApi;
                formApiRef.current = formApi;
                return (
                    <form onSubmit={submitForm} id="stripeWithdrawForm">
                        <Modal visible={true} onClickBackdrop={onCloseClick} className="stripeWithdrawSidebar" dialogClassName="mt0 mb0">
                            <div className="modal-header">
                                <div className="text-medium">Withdraw/Refund Player</div>
                                <div className="close-icon" onClick={onCloseClick}><i className="utr utr-close" /></div>
                            </div>
                            {step === 1 && (
                                <div className="modal-body">
                                    <div className="mb24 text-dark">Adjust Registration for {withdrawPlayer.firstName} {withdrawPlayer.lastName}</div>
                                    <div className="row">
                                        <div className="col-12">
                                            <div className="form-group form-group-xl">
                                                {registeredDivisions.map(d => {
                                                    const divisionRegistration = withdrawPlayer.divisions.find(div => div.id === d.id);
                                                    const isWithdrawn = divisionRegistration?.registrationStatusTypeId === registrationStatusTypeIds.WITHDRAWN;
                                                    const divisionAmount = regPaymentRefund?.divisionAmounts?.find(div => d.isEventEntryFeeDivision && parseInt(div.divisionId) === parseInt(d.id));
                                                    let priceAmount = d.price;
                                                    if (divisionAmount) {
                                                        priceAmount = divisionAmount.price;
                                                    }

                                                    const isNotRefundable = [paymentStatusTypeIds.REFUNDED, paymentStatusTypeIds.REFUNDING, paymentStatusTypeIds.FREE]
                                                        .includes(divisionRegistration?.paymentStatusTypeId);
                                                    const getDivisionOptions = () => {
                                                        if (isWithdrawn) {
                                                            return withdrawnPaidOptions;
                                                        }
                                                        if (isNotRefundable || !d.entryFee) {
                                                            return freeOptions;
                                                        }
                                                        return d.isEventEntryFeeDivision ? entryFeePaidOptions : paidOptions;
                                                    };
                                                    const divisionOptions = getDivisionOptions();

                                                    return (
                                                        <div className={`division-card registered card-border mb16`} key={d.id}>
                                                            <div className="details">
                                                                <div className="text-dark mb8">{titleCase(d.name)}<span className="price float-right">{priceAmount}</span></div>
                                                                <div className="description w-100 mt8">
                                                                    <Select
                                                                        formGroupCssClass={`mb0`}
                                                                        id={d.id}
                                                                        emptyOption={`No Action`}
                                                                        field={`${d.id}`}
                                                                        options={divisionOptions}
                                                                        attributeToSave={`id`}
                                                                        themeV3
                                                                    />
                                                                </div>
                                                                <div className={`mt24 d-flex flex-row align-self-stretch`}>
                                                                    <div className={`flex-grow`}>
                                                                        Registration: {getPlayerRegStatus(divisionRegistration?.registrationStatusTypeId).label}
                                                                    </div>
                                                                    <div className={`flex-grow`}>
                                                                        Payment: {getLabelByTypeId(paymentStatusTypes, divisionRegistration?.paymentStatusTypeId)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {step === 2 && (
                                <div className="modal-body">
                                    <p className="text-bold text-dark">Confirm registration change</p>
                                    <p>Please review the change before confirming. This cannot be undone.</p>
                                    <p className="mt40">The player will be withdrawn from the following divisions:</p>
                                    <ul>
                                        {displayDivisions(values, true)}
                                    </ul>
                                    {!!filterDivisions(values, false).length && (
                                        <>
                                            <p>The player will be refunded for the following divisions:</p>
                                            <ul>
                                                {displayDivisions(values, false)}
                                            </ul>
                                            {displayRefunds()}
                                        </>
                                    )}
                                </div>
                            )}
                            <div className="modal-footer bt0">
                                <div className="container w-100">
                                    <div className="row">
                                        {step === 2 && (
                                            <div className="col-6">
                                                <button
                                                    type="button"
                                                    className={`btn btn-default btn-xl text-uppercase btn-block`}
                                                    onClick={handlePreviousStepClick}
                                                >
                                                    Back
                                                </button>
                                            </div>
                                        )}
                                        <div className={`col-${step === 1 ? '12' : '6'}`}>
                                            {showWithdrawalFeeWaiverCheckbox(values) && step === 1 && (
                                                <CheckBox
                                                    controlled={true}
                                                    id="chargeWithdrawalFee"
                                                    customLabel="Charge Withdrawal Fee"
                                                    checked={chargeWithdrawalFee}
                                                    onChange={handleChargeWithdrawalFeeClick}
                                                    customLabelClass="fs13"
                                                />
                                            )}
                                            <button
                                                type="submit"
                                                className={`btn btn-xl btn-block btn-primary text-uppercase`}
                                                disabled={!objectHasValues(values) || (step === 2 && (regPaymentRefund.loading || isSubmitting))}
                                            >
                                                {step === 1 && 'Next'}
                                                {step === 2 && !isSubmitting && (!filterDivisions(values, false).length ? 'Withdraw' : 'Refund')}
                                                {step === 2 && isSubmitting && (
                                                    'Submitting...'
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Modal>
                    </form>
                );
            }}
        </Form>
    );
};

StripeWithdrawModalV2.propTypes = {
    event: PropTypes.shape({
        id: PropTypes.number.isRequired,
        eventTypeId: PropTypes.number.isRequired,
    }).isRequired,
    withdrawPlayer: PropTypes.shape({
        memberId: PropTypes.number.isRequired,
        divisions: PropTypes.arrayOf(any).isRequired,
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
    }).isRequired,
    confirmPopup: PropTypes.func,
    onCloseClick: PropTypes.func,
    divisions: PropTypes.arrayOf(any),
    currency: PropTypes.shape({
        symbol: PropTypes.string.isRequired,
        // other properties of the currency object if any
    }).isRequired,
    regPaymentRefund: PropTypes.objectOf(any).isRequired,
};

StripeWithdrawModalV2.defaultProps = {
    divisions: [],
    confirmPopup: () => { },
    onCloseClick: () => { },
};

export default StripeWithdrawModalV2;
